import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, of } from 'rxjs';
import { catchError, concatMap, first, map, switchMap, tap } from 'rxjs/operators';

import { PurchaseConfirmedEvent } from '../../../models/events/purchase-confirmed.model';
import { ZenitEventName } from '../../../models/zenit-general.model';
import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';
import { ZenitEventError, ZenitEventSuccess } from '../../actions/zenit.actions';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { selectConfirmation } from '@features/orders/order-confirmation/infrastructure/state/order-confirmation.selectors';
import { Checkout } from '@shared/purchase/store/purchase.actions';

const ZENIT_PURCHASE_EVENT_PENDING = 'ZenitPurchaseEventPending';

// #region Functionality
const markPurchaseEventPendig = (
    coreStorageService: CoreStorageService,
    orderId: string
): void => {
    coreStorageService.setValue(ZENIT_PURCHASE_EVENT_PENDING, orderId, 'SESSION', 'ANALYTICS');
};

export const clearPurchaseEventPending = (coreStorageService: CoreStorageService): void => {
    coreStorageService.removeValue(ZENIT_PURCHASE_EVENT_PENDING, 'SESSION');
};

export const isPurchaseEventPending = (coreStorageService: CoreStorageService, orderId: number): boolean => {
    return coreStorageService.getValue<number>(ZENIT_PURCHASE_EVENT_PENDING, 'SESSION') === orderId;
};
// #endregion

// #region Effects
export const MarkPurchaseEventPendig$ = createEffect((
    actions$ = inject(Actions),
    storageService = inject(CoreStorageService),
) => actions$.pipe(
    ofType(Checkout),
    tap(({ orderId }) => {
        markPurchaseEventPendig(storageService, orderId);
    })
), { dispatch: false, functional: true });

export const PurchaseConfirmedEvent$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    coreStorageService = inject(CoreStorageService),
    zenitEventApiService = inject(ZenitEventApiService),
    zenitEventParserService = inject(ZenitEventParserService),
    platformService = inject(CorePlatformService),
) => {
    return actions$.pipe(
        ofType(SetPage),
        filter(() => platformService.isBrowser()),
        // eslint-disable-next-line no-secrets/no-secrets
        filter((action) => action.page === 'ItxAngularOrderConfirmationPage' || action.page === 'ItxAngularOrderConfirmationNodePage'),
        map(() => store.selectSignal(getCmsConfigValue('enabled_zenit'))()),
        filter(enabledZenit => enabledZenit),
        // eslint-disable-next-line rxjs/no-unsafe-switchmap
        switchMap(() => store.select(selectConfirmation).pipe(
            first((value) => !!value),
        )),
        filter(({ id }) => isPurchaseEventPending(coreStorageService, id)),
        map(() => zenitEventParserService.getEventObject<PurchaseConfirmedEvent>(ZenitEventName.PurchaseConfirmed)),
        concatMap(purchaseConfirmed => zenitEventApiService.postEvent$(purchaseConfirmed, 'purchase-confirmed.input.v1').pipe(
            map(() => ZenitEventSuccess<PurchaseConfirmedEvent>()({ eventData: purchaseConfirmed })),
            catchError((error) => of(ZenitEventError({ error }))),
        )),
    );
}, { functional: true });

export const ClearPurchaseEventPending$ = createEffect((
    actions$ = inject(Actions),
    coreStorageService = inject(CoreStorageService),
) => {
    return actions$.pipe(
        ofType(ZenitEventSuccess<PurchaseConfirmedEvent>()),
        filter(({ eventData }) => eventData.eventName === ZenitEventName.PurchaseConfirmed),
        tap(() => { clearPurchaseEventPending(coreStorageService); }),
    );
}, { dispatch: false, functional: true });
// #endregion

export const PurchaseConfirmedEventEffects = {
    ClearPurchaseEventPending$,
    MarkPurchaseEventPendig$,
    PurchaseConfirmedEvent$,
};

export { ZENIT_PURCHASE_EVENT_PENDING };
